/**
 * 🧠 Service pour interagir avec le Big Data Expert Agent (Backend)
 * Gère les sessions IA, queries intelligentes et insights
 */

import { authFetch } from '@/utils/api-headers';
import { API_URL } from '@/lib/config';
import { debugLog } from '../utils/debug-logger';
import appLogger from '../utils/logger';

export interface ExpertSession {
  sessionId: string; // String ID pour référence interne (e.g. expert_1765836760920_b78e2628)
  supabaseSessionId: string; // ✅ UUID réel de big_data_sessions pour lier les insights
  jobId: string;
  userId: string;
  understandingLevel: string;
  contextSize: number;
  learnedAt: string;
  summary: {
    rows: number;
    columns: number;
    patterns: number;
    insights: number;
  };
}

export interface QueryResult {
  answer: string;
  confidence: number;
  sqlQuery?: string;
  resultCount?: number;
  execution_time_ms?: number;
  visualizations?: any[];
  insights?: Insight[];
  patterns?: Pattern[];
  recommendations?: string[];
  exportFile?: {
    filename: string;
    url: string;
    rowCount: number;
    size: number;
  };
}

export interface Insight {
  type: 'pattern' | 'anomaly' | 'correlation' | 'trend' | 'outlier';
  title: string;
  description: string;
  confidence: number;
  data?: any;
  severity?: 'low' | 'medium' | 'high';
}

export interface Pattern {
  name: string;
  description: string;
  confidence: number;
  occurrences: number;
}

export class BigDataExpertService {
  /**
   * 🚀 Initialiser une session expert pour un dataset
   * Claude "apprend" le dataset et construit son contexte
   */
  static async initializeSession(jobId: string, userId: string): Promise<ExpertSession> {
    debugLog('[🧠 ExpertService] Initializing session for job:', jobId);

    const resp = await authFetch(`${API_URL}/api/big-data-server/expert/init`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jobId, userId }),
    });

    if (!resp.ok) {
      const txt = await resp.text();
      appLogger.error('[❌ ExpertService] Failed to init session:', txt);
      throw new Error(`Failed to initialize expert session: ${txt}`);
    }

    const json = await resp.json();
    debugLog('[✅ ExpertService] Session initialized:', json.sessionId);
    debugLog('[🔗 ExpertService] Supabase session UUID:', json.supabaseSessionId);
    debugLog('[📊 ExpertService] Understanding level:', json.understandingLevel);
    debugLog('[📦 ExpertService] Context size:', json.contextSize, 'chars');
    
    return {
      sessionId: json.sessionId,
      supabaseSessionId: json.supabaseSessionId, // ✅ UUID de big_data_sessions
      jobId: json.jobId,
      userId: json.userId,
      understandingLevel: json.understandingLevel,
      contextSize: json.contextSize,
      learnedAt: json.learnedAt,
      summary: json.summary,
    };
  }

  /**
   * 💬 Poser une question au dataset avec IA contextuelle
   * Utilise le Resilient Query Engine (5 fallback strategies)
   */
  static async query(
    sessionId: string,
    query: string,
    userId?: string
  ): Promise<QueryResult> {
    debugLog('[💬 ExpertService] Sending query:', query);

    const resp = await authFetch(`${API_URL}/api/big-data-server/expert/query`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, query, userId }),
    });

    if (!resp.ok) {
      const txt = await resp.text();
      appLogger.error('[❌ ExpertService] Query failed:', txt);
      
      // ✅ NO LOCALSTORAGE - Sessions are in Supabase only
      
      throw new Error(`Query failed: ${txt}`);
    }

    const json = await resp.json();
    debugLog('[✅ ExpertService] Query result:', {
      confidence: json.confidence,
      hasSQL: !!json.sqlQuery,
      resultCount: json.resultCount,
      insights: json.insights?.length || 0,
      patterns: json.patterns?.length || 0,
    });

    return {
      answer: json.answer,
      confidence: json.confidence,
      sqlQuery: json.sqlQuery || json.sql,
      resultCount: json.resultCount,
      execution_time_ms: json.execution_time_ms,
      visualizations: json.visualizations,
      insights: json.insights,
      patterns: json.patterns,
      recommendations: json.recommendations,
      exportFile: json.exportFile || undefined,
    };
  }

  /**
   * 📊 Obtenir les statistiques d'une session
   */
  static async getSessionStats(sessionId: string): Promise<any> {
    debugLog('[📊 ExpertService] Getting session stats:', sessionId);

    const resp = await authFetch(
      `${API_URL}/api/big-data-server/expert/session/${sessionId}/stats`,
      { method: 'GET' }
    );

    if (!resp.ok) {
      if (resp.status === 404) {
        appLogger.warn('[⚠️ ExpertService] Session not found');
        return null;
      }
      const txt = await resp.text();
      appLogger.error('[❌ ExpertService] Failed to get stats:', txt);
      throw new Error(`Failed to get session stats: ${txt}`);
    }

    const json = await resp.json();
    debugLog('[✅ ExpertService] Session stats:', json.stats);
    return json.stats;
  }

  // ✅ REMOVED: All localStorage methods - Sessions persisted in Supabase only
}

export default BigDataExpertService;

